import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, AlertCircle, Eye } from 'lucide-react';
import { motion } from 'framer-motion';
import { LawVoLogo } from '../components/LawVoLogo';
export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const {
    login,
    loginAsPreview
  } = useAuth();
  const navigate = useNavigate();
  // Only expose the no-login preview entry on hosts where the real LawVo
  // login API is unreachable due to CORS (e.g. Magic Patterns previews,
  // localhost). On any lawvo.com host this block stays hidden so we never
  // ship a bypass button to production users.
  const isOffDomain = typeof window !== 'undefined' && !/(^|\.)lawvo\.com$/i.test(window.location.hostname);
  const handlePreviewLogin = () => {
    setError(null);
    loginAsPreview();
    navigate('/');
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-514">
      <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-515">
        <div className="flex justify-center mb-6" data-id="element-516">
          <LawVoLogo size="lg" data-id="element-517" />
        </div>
        <h2 className="mt-6 text-center text-2xl font-bold tracking-tight text-slate-900" data-id="element-518">
          LawVo Workspace
        </h2>
        <p className="mt-2 text-center text-sm text-slate-500" data-id="element-519">
          Sign in with your LawVo credentials to continue
        </p>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4
    }} className="mt-8 sm:mx-auto sm:w-full sm:max-w-md" data-id="element-520">
        
        <div className="bg-white py-8 px-4 shadow-sm sm:rounded-xl sm:px-10 border border-slate-200" data-id="element-521">
          <form className="space-y-6" onSubmit={handleSubmit} data-id="element-522">
            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-id="element-523">
                <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-524" />
                <p className="text-sm text-red-700" data-id="element-525">{error}</p>
              </div>}

            <div data-id="element-526">
              <label htmlFor="email" className="block text-sm font-medium text-slate-700" data-id="element-527">
                
                Email address
              </label>
              <div className="mt-1" data-id="element-528">
                <input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} className="block w-full appearance-none rounded-lg border border-slate-300 px-3 py-2 placeholder-slate-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm" data-id="element-529" />
                
              </div>
            </div>

            <div data-id="element-530">
              <label htmlFor="password" className="block text-sm font-medium text-slate-700" data-id="element-531">
                
                Password
              </label>
              <div className="mt-1" data-id="element-532">
                <input id="password" name="password" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} className="block w-full appearance-none rounded-lg border border-slate-300 px-3 py-2 placeholder-slate-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm" data-id="element-533" />
                
              </div>
            </div>

            <div data-id="element-534">
              <button type="submit" disabled={isLoading} className="flex w-full justify-center items-center gap-2 rounded-lg border border-transparent bg-indigo-600 py-2.5 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-535">
                
                {isLoading ? <>
                    <Loader2 className="w-4 h-4 animate-spin" data-id="element-536" />
                    Signing in...
                  </> : 'Sign in'}
              </button>
            </div>
          </form>

          {/* Preview / Magic Patterns bypass — only rendered on hosts where
           the real auth endpoint is blocked by CORS (anything that isn't
           lawvo.com). Never appears in production. */}
          {isOffDomain && <div className="mt-6" data-id="element-537">
              <div className="relative" data-id="element-538">
                <div className="absolute inset-0 flex items-center" aria-hidden data-id="element-539">
                  <div className="w-full border-t border-slate-200" data-id="element-540" />
                </div>
                <div className="relative flex justify-center text-xs uppercase tracking-wider" data-id="element-541">
                  <span className="bg-white px-2 text-slate-400 font-medium" data-id="element-542">
                    Preview Mode
                  </span>
                </div>
              </div>
              <button type="button" onClick={handlePreviewLogin} className="mt-4 flex w-full justify-center items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 py-2.5 px-4 text-sm font-medium text-slate-700 hover:bg-slate-100 hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 transition-colors" data-id="element-543">
              
                <Eye className="w-4 h-4" data-id="element-544" />
                Continue without login (preview)
              </button>
              <p className="mt-2 text-center text-[11px] text-slate-400 leading-relaxed" data-id="element-545">
                Visible only outside lawvo.com — for browsing the UI when the
                login API is blocked by CORS. Backend calls still require real
                credentials.
              </p>
            </div>}
        </div>
        <p className="mt-6 text-center text-xs text-slate-400" data-id="element-546">
          Secure access to LawVo translation tools
        </p>
      </motion.div>
    </div>;
};